import { Loader2, Monitor, Save, Shield, Upload, User as UserIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { storage } from "@/apis/config/firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePlans } from "@/features/plans/hooks/usePlans";
import { resolveAssignedPlan } from "@/features/plans/services/plansService";
import type { Plan } from "@/features/plans/schemas/plan";
import { PLAN_APP_LABEL, PLAN_PERIOD_LABEL } from "@/features/plans/schemas/plan";
import { AssignRolesDialog } from "@/features/rbac/components/AssignRolesDialog";
import { useAssignment } from "@/features/rbac/hooks/useAssignments";
import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import { useRoles } from "@/features/rbac/hooks/useRoles";
import { useUpdateUserProfile } from "@/features/users/hooks/useUsers";
import type {
  User,
  UserProfileUpdate,
} from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";

import { EmailPreviewDialog } from "./EmailPreviewDialog";

interface UserEditDialogProps {
  user: User | null;
  open: boolean;
  onClose: () => void;
}

export function UserEditDialog({ user, open, onClose }: UserEditDialogProps) {
  // Estado del formulario de perfil
  const [profile, setProfile] = useState<UserProfileUpdate>({});

  // Estado para el preview de correo
  const [showPreview, setShowPreview] = useState(false);

  // Mensajes de feedback
  const [profileResult, setProfileResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const updateProfileMutation = useUpdateUserProfile();
  const { data: plans = [] } = usePlans();
  const { can } = usePermissions();
  // Conceder acceso al panel y asignar roles son la misma clase de decisión
  // sensible, así que comparten permiso.
  const canGrantAccess = can("roles:assign");
  const [showAssignRoles, setShowAssignRoles] = useState(false);
  const { data: allRoles = [] } = useRoles();
  const { data: assignment } = useAssignment(user?.uid ?? null);

  // Sin asignación explícita, los roles efectivos son los heredados de
  // `permission_admin`, cuyos valores coinciden con los ids de rol del sistema.
  const usesLegacyPermissions = !assignment || assignment.roleIds.length === 0;
  const effectiveRoleIds = usesLegacyPermissions
    ? (user?.permission_admin ?? [])
    : assignment.roleIds;
  const assignedRoleNames = effectiveRoleIds.map(
    (roleId) => allRoles.find((role) => role.id === roleId)?.name ?? roleId
  );
  const isInstitution = user?.type === "institution";

  const availablePlans = useMemo(() => {
    const userType = user?.type ?? profile.type;
    const currentPlanId = profile.planId;

    const matchesUserType = (plan: Plan) => {
      if (!userType || userType === "admin") return true;
      return plan.app === userType;
    };

    const filtered = plans.filter(matchesUserType);

    if (!currentPlanId) return filtered;

    const currentPlan = resolveAssignedPlan(currentPlanId, plans);
    if (
      currentPlan &&
      !filtered.some((plan) => plan.docId === currentPlan.docId)
    ) {
      return [...filtered, currentPlan];
    }

    return filtered;
  }, [plans, user?.type, profile.type, profile.planId]);

  const selectedPlanValue = useMemo(() => {
    if (!profile.planId) return "none";
    const assigned = resolveAssignedPlan(profile.planId, plans);
    return assigned?.docId ?? "none";
  }, [profile.planId, plans]);

  const formatPlanLabel = (plan: Plan) => {
    const appLabel = plan.app ? PLAN_APP_LABEL[plan.app] : "Sin app";
    const periodLabel = PLAN_PERIOD_LABEL[plan.period].toLowerCase();
    return `${plan.name} · ${appLabel} · ${periodLabel} · $${plan.price}`;
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Inicializar formulario solo cuando cambia el usuario (UID) o se abre por primera vez
  useEffect(() => {
    if (user && user.uid !== (profile as any).uid) {
      setProfile({
        ...(user as any),
        name: user.name ?? "",
        forename: user.forename ?? "",
        surname: user.surname ?? "",
        phone_support: (user as any).phone_support ?? "",
        email_support: (user as any).email_support ?? "",
        category: user.category ?? "",
        type: user.type,
        logo_email: user.logo_email ?? "",
        vm_admin: user.vm_admin ?? false,
        permission_admin: user.permission_admin ?? [],
        admin_lab: (user as any).admin_lab ?? false,
        api_whatsapp_active: (user as any).api_whatsapp_active ?? false,
        report_whatsapp_active: (user as any).report_whatsapp_active ?? false,
        active_imaging: user.active_imaging ?? false,
        admin_imaging: user.admin_imaging ?? false,
        block_upload: user.block_upload ?? false,
        days_link: user.days_link ?? 31,
        notification: user.notification ?? {
          email: true,
          push: false,
          sms: false,
          ws: false,
        },
      });
      setProfileResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const handleClose = () => {
    setProfileResult(null);
    onClose();
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setLogoUploading(true);
    setProfileResult(null);

    try {
      // Ruta persistente por usuario para que siempre reemplace
      const storageRef = ref(storage, `logos/users/${user.uid}/logo_email`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      setProfile((prev) => ({ ...prev, logo_email: url }));
      setProfileResult({
        type: "success",
        message: "Logo cargado correctamente. Recuerda guardar los cambios.",
      });
    } catch (err) {
      console.error(err);
      setProfileResult({
        type: "error",
        message: "Error al subir el logo al servidor.",
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setProfileResult(null);

    // Omitir campos inmutables del payload
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { uid, email, created_at, ...payload } = profile as any;

    try {
      await updateProfileMutation.mutateAsync({
        uid: user.uid,
        data: payload,
      });
      setProfileResult({
        type: "success",
        message: `Perfil actualizado exitosamente.`,
      });
    } catch (err) {
      setProfileResult({
        type: "error",
        message: err instanceof Error ? err.message : "Error al actualizar el perfil.",
      });
    }
  };

  const updateField = (key: keyof UserProfileUpdate, value: any) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <UserIcon className="h-6 w-6 text-primary" />
            Edición de Usuario
          </DialogTitle>
          <DialogDescription>
            {getUserFullName(user)} — {user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* ── Perfil Content ── */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {isInstitution ? (
                <div className="col-span-2 space-y-1">
                  <Label htmlFor="name">Nombre Institución</Label>
                  <Input
                    id="name"
                    value={profile.name ?? ""}
                    onChange={(e) => updateField("name", e.target.value)}
                    placeholder="Nombre del centro o institución"
                  />
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="forename">Nombre(s)</Label>
                    <Input
                      id="forename"
                      value={profile.forename ?? ""}
                      onChange={(e) => updateField("forename", e.target.value)}
                      placeholder="Nombres del usuario"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="surname">Apellidos</Label>
                    <Input
                      id="surname"
                      value={profile.surname ?? ""}
                      onChange={(e) => updateField("surname", e.target.value)}
                      placeholder="Apellidos del usuario"
                    />
                  </div>
                </>
              )}

              <div className="space-y-1">
                <Label htmlFor="type">Tipo de Usuario</Label>
                <Input
                  id="type"
                  value={profile.type ?? ""}
                  onChange={(e) => updateField("type", e.target.value)}
                  placeholder="Ej: physician, patient..."
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="category">Categoría / Especialidad</Label>
                <Input
                  id="category"
                  value={profile.category ?? ""}
                  onChange={(e) => updateField("category", e.target.value)}
                  placeholder="Ej: Radiología"
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="planId">Plan</Label>
                <Select
                  value={selectedPlanValue}
                  onValueChange={(value) =>
                    updateField("planId", value === "none" ? null : value)
                  }
                >
                  <SelectTrigger id="planId" className="w-full">
                    <SelectValue placeholder="Sin plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin plan</SelectItem>
                    {availablePlans.map((plan) => (
                      <SelectItem key={plan.docId} value={plan.docId}>
                        {formatPlanLabel(plan)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="phone_support">Teléfono Soporte</Label>
                <Input
                  id="phone_support"
                  value={profile.phone_support ?? ""}
                  onChange={(e) => updateField("phone_support", e.target.value)}
                  placeholder="+58 412..."
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="email_support">Email Soporte</Label>
                <Input
                  id="email_support"
                  value={profile.email_support ?? ""}
                  onChange={(e) => updateField("email_support", e.target.value)}
                  placeholder="soporte@ejemplo.com"
                />
              </div>

              {/* Logo Email - File Upload - Solo para no pacientes */}
              {profile.type !== "patient" && (
                <div className="col-span-2 space-y-2">
                  <Label>Logo para Correos</Label>
                  <div className="flex items-center gap-4">
                    {profile.logo_email ? (
                      <div className="h-16 w-32 border rounded bg-muted flex items-center justify-center overflow-hidden">
                        <img
                          src={profile.logo_email}
                          alt="Logo"
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="h-16 w-32 border border-dashed rounded bg-muted/50 flex items-center justify-center text-xs text-muted-foreground text-center px-2">
                        Sin logo
                      </div>
                    )}
                    <div className="flex-1 flex gap-2">
                      <div className="flex-1">
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleLogoUpload}
                          accept="image/*"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={logoUploading}
                          className="w-full"
                        >
                          {logoUploading ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          {profile.logo_email ? "Cambiar Logo" : "Subir Logo"}
                        </Button>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Se subirá al servidor y reemplazará el anterior.
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="self-start"
                        title="Previsualizar correo"
                        onClick={() => setShowPreview(true)}
                      >
                        Preview
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Configuración de Institución ── */}
            {isInstitution && (
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Monitor className="h-5 w-5 text-teal-600" />
                  <Label className="text-base font-bold">
                    Configuración de Institución
                  </Label>
                </div>

                <Card className="bg-muted/30 border-none shadow-none">
                  <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center justify-between p-2 rounded-md hover:bg-background/50 transition-colors">
                      <Label htmlFor="active_imaging" className="cursor-pointer text-sm">
                        Activar Imagenología
                      </Label>
                      <input
                        type="checkbox"
                        id="active_imaging"
                        checked={!!profile.active_imaging}
                        onChange={(e) => updateField("active_imaging", e.target.checked)}
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-md hover:bg-background/50 transition-colors">
                      <Label htmlFor="admin_imaging" className="cursor-pointer text-sm">
                        Administrador Imagen
                      </Label>
                      <input
                        type="checkbox"
                        id="admin_imaging"
                        checked={!!profile.admin_imaging}
                        onChange={(e) => updateField("admin_imaging", e.target.checked)}
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-md hover:bg-background/50 transition-colors">
                      <Label htmlFor="admin_lab" className="cursor-pointer text-sm">
                        Administrador Lab
                      </Label>
                      <input
                        type="checkbox"
                        id="admin_lab"
                        checked={!!profile.admin_lab}
                        onChange={(e) => updateField("admin_lab", e.target.checked)}
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-md hover:bg-background/50 transition-colors">
                      <Label htmlFor="block_upload" className="cursor-pointer text-sm">
                        Bloquear Carga
                      </Label>
                      <input
                        type="checkbox"
                        id="block_upload"
                        checked={!!profile.block_upload}
                        onChange={(e) => updateField("block_upload", e.target.checked)}
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-md hover:bg-background/50 transition-colors">
                      <Label
                        htmlFor="api_whatsapp_active"
                        className="cursor-pointer text-sm"
                      >
                        WhatsApp API Activo
                      </Label>
                      <input
                        type="checkbox"
                        id="api_whatsapp_active"
                        checked={!!profile.api_whatsapp_active}
                        onChange={(e) =>
                          updateField("api_whatsapp_active", e.target.checked)
                        }
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded-md hover:bg-background/50 transition-colors">
                      <Label
                        htmlFor="report_whatsapp_active"
                        className="cursor-pointer text-sm"
                      >
                        Reportes WhatsApp
                      </Label>
                      <input
                        type="checkbox"
                        id="report_whatsapp_active"
                        checked={!!profile.report_whatsapp_active}
                        onChange={(e) =>
                          updateField("report_whatsapp_active", e.target.checked)
                        }
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                      />
                    </div>

                    <div className="col-span-1 sm:col-span-2 grid grid-cols-2 gap-4 mt-2 border-t pt-3">
                      <div className="space-y-1">
                        <Label htmlFor="days_link" className="text-xs">
                          Días de enlace activo
                        </Label>
                        <Input
                          id="days_link"
                          type="number"
                          size={1}
                          value={profile.days_link ?? 31}
                          onChange={(e) =>
                            updateField("days_link", parseInt(e.target.value) || 1)
                          }
                          className="h-8"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Notificaciones</Label>
                        <div className="flex gap-4">
                          <div className="flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              id="notif_email"
                              checked={!!profile.notification?.email}
                              onChange={(e) =>
                                updateField("notification", {
                                  ...profile.notification,
                                  email: e.target.checked,
                                })
                              }
                              className="h-4 w-4 rounded border-gray-300 text-primary"
                            />
                            <Label
                              htmlFor="notif_email"
                              className="text-[10px] cursor-pointer"
                            >
                              Email
                            </Label>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="checkbox"
                              id="notif_ws"
                              checked={!!profile.notification?.ws}
                              onChange={(e) =>
                                updateField("notification", {
                                  ...profile.notification,
                                  ws: e.target.checked,
                                })
                              }
                              className="h-4 w-4 rounded border-gray-300 text-primary"
                            />
                            <Label
                              htmlFor="notif_ws"
                              className="text-[10px] cursor-pointer"
                            >
                              WhatsApp
                            </Label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Checkbox VM Admin al final - Solo SuperAdmins */}
            {canGrantAccess && (
              <div className="flex items-center justify-between p-4 bg-primary/5 rounded-xl border border-primary/10">
                <div className="space-y-0.5">
                  <Label htmlFor="vm_admin" className="text-base font-bold text-primary">
                    Acceso Administrador VM
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    Habilita el acceso al panel administrativo de ViewMed
                  </p>
                </div>
                <input
                  type="checkbox"
                  id="vm_admin"
                  checked={!!profile.vm_admin}
                  onChange={(e) => updateField("vm_admin", e.target.checked)}
                  className="h-6 w-6 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
              </div>
            )}

            {/* ── Roles del panel (colección rbac_assignments) ── */}
            {profile.vm_admin && canGrantAccess && (
              <div className="space-y-3 pt-4 border-t animate-in fade-in slide-in-from-top-2 duration-300">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <Label className="text-base font-bold">Roles del panel</Label>
                </div>

                <p className="text-sm text-muted-foreground">
                  {assignedRoleNames.length > 0
                    ? assignedRoleNames.join(" · ")
                    : "Sin roles asignados."}
                </p>

                {usesLegacyPermissions && (
                  <p className="text-xs text-muted-foreground">
                    Sus permisos se resuelven todavía por la vía heredada. Al asignarle
                    roles se creará una asignación explícita.
                  </p>
                )}

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAssignRoles(true)}
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Gestionar roles
                </Button>
              </div>
            )}

            {/* Feedback y Footer */}
            {profileResult && (
              <div
                className={`rounded-lg p-4 text-sm font-medium ${
                  profileResult.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {profileResult.message}
              </div>
            )}

            <DialogFooter className="bg-background pt-4 border-t sticky bottom-0 z-10 w-full">
              <Button variant="ghost" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleSaveProfile}
                disabled={updateProfileMutation.isPending || logoUploading}
                className="px-8 shadow-md"
              >
                {updateProfileMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {!updateProfileMutation.isPending && <Save className="h-4 w-4 mr-2" />}
                Guardar Perfil
              </Button>
            </DialogFooter>
          </div>
        </div>

        {/* Modal de Preview de Correo */}
        <AssignRolesDialog
        subject={
          user
            ? {
                uid: user.uid,
                email: user.email ?? null,
                displayName: getUserFullName(user),
                legacyPermissions: user.permission_admin ?? [],
              }
            : null
        }
        open={showAssignRoles}
        onClose={() => setShowAssignRoles(false)}
      />

      <EmailPreviewDialog
          open={showPreview}
          onClose={() => setShowPreview(false)}
          logoUrl={profile.logo_email}
          institutionName={profile.name || user.name}
          supportEmail={profile.email_support || (user as any).email_support}
        />
      </DialogContent>
    </Dialog>
  );
}
